'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import * as HubSpotFeature from 'marketplace-ui-apps-core/common/constants/groupTags/HubSpotFeature';
import * as PlanType from 'marketplace-ui-common/constants/pricings/PlanType';
import * as SupportSource from 'marketplace-ui-common/constants/search/SupportSource';
import { parse as parseQuery, stringify as stringifyQuery } from 'hub-http/helpers/params';
import Immutable from 'immutable';
import * as CategoryV2 from 'marketplace-ui-apps-core/common/constants/groupTags/CategoryV2';
import { getHasAppCertificationCredential } from 'marketplace-ui-apps-core/common/utils/Certification';
import * as DataSync from 'marketplace-ui-apps-core/details/constants/dataSync/Sync';
import * as TierGroups from 'marketplace-ui-common/constants/groupTags/TierGroups';
import * as Tiers from 'marketplace-ui-common/constants/groupTags/Tiers';
import * as FilterKey from '../constants/search/FilterKey';
import { APP_LANGUAGES } from '../constants/search/FilterKey';
import * as SortField from '../constants/search/SortField';
import * as SortOrder from '../constants/search/SortOrder';
import { getAllCategoriesUrl, getCategoryV2Url } from '../utils/urls';
const EMPTY_SET = Immutable.Set();
const {
  CATEGORY_V2,
  CREDENTIALS,
  SYNC,
  INTEGRATIONS,
  PLAN_COMPATIBLE,
  PRICING,
  SUPPORT_SOURCE,
  SUPPORT_LANGUAGES,
  TIERS,
  HUBSPOT_FEATURES,
  OPS_HUB,
  SEARCH,
  SORTS,
  SORT_FIELD,
  SORT_ORDER,
  SHOW_PREFERRED_LANG_FIRST
} = FilterKey;
const CATEGORY_V2_SET = Immutable.Set(Object.values(CategoryV2));
const TIERS_SET = Immutable.Set(Object.values(Tiers));
const HUBSPOT_FEATURES_SET = Immutable.Set(Object.values(HubSpotFeature));
const DATA_SYNCS_SET = Immutable.Set(Object.values(DataSync));
const SUPPORT_SOURCE_SET = Immutable.Set(Object.values(SupportSource));
const PLAN_TYPE_SET = Immutable.Set(Object.values(PlanType));
const SORT_ORDER_SET = Immutable.Set(Object.values(SortOrder));
const SORT_FIELD_SET = Immutable.Set(Object.values(SortField));
const isOpsHubTier = tier => TierGroups.OPERATIONS.includes(tier);
const filterKeyOrder = Immutable.OrderedSet([CATEGORY_V2, PRICING, PLAN_COMPATIBLE, TIERS, HUBSPOT_FEATURES, INTEGRATIONS, CREDENTIALS, SYNC, APP_LANGUAGES, SUPPORT_LANGUAGES, SUPPORT_SOURCE, SEARCH, SORT_FIELD, SORT_ORDER, SHOW_PREFERRED_LANG_FIRST]);
const filterValuesSetMap = Immutable.Map({
  [CATEGORY_V2]: CATEGORY_V2_SET,
  [TIERS]: TIERS_SET,
  [HUBSPOT_FEATURES]: HUBSPOT_FEATURES_SET,
  [SYNC]: DATA_SYNCS_SET,
  [SUPPORT_SOURCE]: SUPPORT_SOURCE_SET,
  [PRICING]: PLAN_TYPE_SET,
  [SORT_FIELD]: SORT_FIELD_SET,
  [SORT_ORDER]: SORT_ORDER_SET
});
const filterKeyEventPropertyMap = Immutable.Map({
  [CATEGORY_V2]: 'category',
  [PRICING]: 'paymentType',
  [PLAN_COMPATIBLE]: 'isCompatible',
  [TIERS]: 'tier',
  [HUBSPOT_FEATURES]: 'tool',
  [INTEGRATIONS]: 'integration',
  [CREDENTIALS]: 'listingStatus',
  [SYNC]: 'integrationsSyncAccess',
  [APP_LANGUAGES]: 'appLanguages',
  [SUPPORT_LANGUAGES]: 'language',
  [SUPPORT_SOURCE]: 'provider',
  [SORT_FIELD]: 'sortField',
  [SORT_ORDER]: 'sortOrder',
  [SEARCH]: 'search',
  [SHOW_PREFERRED_LANG_FIRST]: 'showPreferredLanguageFirst'
});
const filterKeyQueryKeyMap = Immutable.Map({
  [CATEGORY_V2]: 'category'
});
const getFilterKeyByQueryKey = queryKey => filterKeyQueryKeyMap.flip().get(queryKey);
const PARAM_PREFIX = 'eco_';
const filterDefaults = {
  [CATEGORY_V2]: EMPTY_SET,
  [PRICING]: null,
  [PLAN_COMPATIBLE]: false,
  [TIERS]: EMPTY_SET,
  [HUBSPOT_FEATURES]: EMPTY_SET,
  [INTEGRATIONS]: EMPTY_SET,
  [CREDENTIALS]: EMPTY_SET,
  [SYNC]: null,
  [APP_LANGUAGES]: EMPTY_SET,
  [SUPPORT_LANGUAGES]: EMPTY_SET,
  [SUPPORT_SOURCE]: null,
  [SEARCH]: '',
  [SORT_FIELD]: null,
  [SORT_ORDER]: null,
  [SHOW_PREFERRED_LANG_FIRST]: false
};
class StorefrontFilters extends Immutable.Record(filterDefaults) {
  static from(filters) {
    const {
        [CATEGORY_V2]: categoryV2,
        [TIERS]: tiers,
        [HUBSPOT_FEATURES]: hubspotFeatures,
        [INTEGRATIONS]: integrations,
        [SUPPORT_LANGUAGES]: supportLanguages
      } = filters,
      restFilters = _objectWithoutPropertiesLoose(filters, [CATEGORY_V2, TIERS, HUBSPOT_FEATURES, INTEGRATIONS, SUPPORT_LANGUAGES].map(_toPropertyKey));
    const mappedFilters = Object.assign({}, restFilters, {
      [CATEGORY_V2]: Immutable.Set(Array.isArray(categoryV2) ? categoryV2 : []),
      [TIERS]: Immutable.Set(Array.isArray(tiers) ? tiers : []),
      [HUBSPOT_FEATURES]: Immutable.Set(Array.isArray(hubspotFeatures) ? hubspotFeatures : []),
      [INTEGRATIONS]: Immutable.Set(Array.isArray(integrations) ? integrations : []),
      [SUPPORT_LANGUAGES]: Immutable.Set(Array.isArray(supportLanguages) ? supportLanguages : [])
    });
    return new StorefrontFilters(mappedFilters);
  }
  fromQueryString(queryString) {
    const {
      [CATEGORY_V2]: categoryV2
    } = this;
    const queryDefaults = _objectWithoutPropertiesLoose(filterDefaults, [CATEGORY_V2].map(_toPropertyKey));
    const parsedQuery = Object.entries(parseQuery(queryString)).reduce((final, [key, value]) => {
      if (key.startsWith(PARAM_PREFIX)) {
        final[key.replace(PARAM_PREFIX, '')] = value;
      }
      return final;
    }, {});
    const mappedQuery = {};
    Object.keys(parsedQuery).forEach(key => {
      const filterKey = getFilterKeyByQueryKey(key) || key;
      if (!Object.values(FilterKey).includes(filterKey)) {
        return;
      }
      const defaultValue = filterDefaults[filterKey];
      const isSet = Immutable.Set.isSet(defaultValue);
      let value = parsedQuery[key];
      switch (filterKey) {
        case PLAN_COMPATIBLE:
          value = value === 'true';
          break;
        case CREDENTIALS:
          if (typeof value === 'string') {
            // Handle single value
            value = Immutable.Set([parseInt(value, 10)]);
          } else if (Array.isArray(value)) {
            // Handle array (including comma-separated values)
            value = Immutable.Set(value.map(v => parseInt(v, 10)));
          } else {
            value = EMPTY_SET;
          }
          break;
        case CATEGORY_V2:
          if (!categoryV2.isEmpty()) {
            value = categoryV2;
          } else {
            const categories = typeof value === 'string' ? value.split(',').filter(Boolean) : Array.isArray(value) ? value : [value];
            value = Immutable.Set(categories);
          }
          break;
        default:
          if (Array.isArray(value) && !isSet) {
            value = value[0];
          }
          if (isSet) {
            value = Immutable.Set(!Array.isArray(value) ? [value] : value);
          }
      }
      if (filterValuesSetMap.has(filterKey)) {
        const valuesSet = filterValuesSetMap.get(filterKey);
        if (Immutable.Set.isSet(value)) {
          value = value.filter(val => valuesSet.includes(val));
          if (value.isEmpty()) return;
        } else if (!valuesSet.includes(value)) {
          return;
        }
      }
      mappedQuery[filterKey] = value;
    });
    return this.merge(Object.assign({}, queryDefaults, mappedQuery)).clean();
  }
  toQuery() {
    const query = this.clean().toJS();
    Object.keys(query).forEach(key => {
      const value = query[key];
      const isArray = Array.isArray(value);
      if (!value || isArray && !value.length) {
        delete query[key];
        return;
      }
      if (isArray) {
        query[key] = value.sort();
      }
    });
    return query;
  }
  toQueryString(collectionSlug) {
    const query = this.toQuery();
    return stringifyQuery(filterKeyOrder.reduce((final, key) => {
      const value = query[key];
      const queryKey = filterKeyQueryKeyMap.get(key, key);
      if (value == null || value === filterDefaults[key]) {
        return final;
      }
      if (key === CATEGORY_V2 && value.length === 1 && !collectionSlug) {
        return final;
      }
      final[`${PARAM_PREFIX}${queryKey}`] = value;
      return final;
    }, {}));
  }
  toSearchQuery(extra = {}) {
    const query = this.toQuery();
    const {
      [CATEGORY_V2]: categoryV2,
      [PRICING]: planType,
      [SEARCH]: search,
      [SORT_FIELD]: sortField,
      [SORT_ORDER]: sortOrder = SortOrder.DESC,
      [TIERS]: tiers
    } = query;
    if (categoryV2) {
      query[CATEGORY_V2] = {
        clause: 'OR',
        values: categoryV2
      };
    }
    if (sortField) {
      query[SORTS] = [{
        sortField,
        sortOrder
      }];
      delete query[SORT_FIELD];
      delete query[SORT_ORDER];
    }
    if (sortField === SortField.PRICE) {
      let priceSortField;
      switch (planType) {
        case PlanType.MONTHLY:
          {
            priceSortField = SortField.MONTHLY_PRICE;
            break;
          }
        case PlanType.YEARLY:
          {
            priceSortField = SortField.YEARLY_PRICE;
            break;
          }
        case PlanType.ONE_TIME:
          {
            priceSortField = SortField.ONE_TIME_PRICE;
            break;
          }
        default:
          break;
      }
      query[SORTS] = [{
        sortField: priceSortField,
        sortOrder
      }];
    }
    if (!sortField && !search) {
      query[SORTS] = [{
        sortField: SortField.RELEVANCE,
        sortOrder: SortOrder.DESC
      }];
    }
    const hasOpsHubTier = tiers && tiers.some(isOpsHubTier);
    if (hasOpsHubTier) {
      query[OPS_HUB] = true;
    }
    if (!query[TIERS] || query[TIERS].length < 1) {
      delete query[TIERS];
    }
    delete query[SHOW_PREFERRED_LANG_FIRST];
    return Object.assign({}, query, extra);
  }
  toEventProperties() {
    const query = this.toQuery();
    const {
      [CREDENTIALS]: credentials,
      [SEARCH]: search
    } = query;
    const properties = {};
    Object.keys(query).forEach(key => {
      const propertyKey = filterKeyEventPropertyMap.get(key);
      if (propertyKey) {
        properties[propertyKey] = query[key];
      }
    });
    if (credentials && getHasAppCertificationCredential(credentials)) {
      properties.listingStatus = 'CERTIFIED';
    }
    if (search) {
      properties.searchLength = search.length;
    }
    return properties;
  }
  toLocation(collectionSlug) {
    const {
      [CATEGORY_V2]: categoryV2
    } = this;
    let pathname;
    if (collectionSlug) {
      pathname = `/${collectionSlug}`;
    } else if (categoryV2.size === 1) {
      pathname = getCategoryV2Url(categoryV2.first());
    } else {
      pathname = getAllCategoriesUrl();
    }
    return {
      pathname,
      search: `?${this.toQueryString(collectionSlug)}`
    };
  }
  toAllCategories() {
    return this.delete(CATEGORY_V2);
  }
  clear(nuke = false) {
    if (nuke) {
      return super.clear();
    }
    return this.withMutations(mutableFilters => {
      mutableFilters.delete(CATEGORY_V2).delete(PRICING).delete(PLAN_COMPATIBLE).delete(TIERS).delete(HUBSPOT_FEATURES).delete(INTEGRATIONS).delete(CREDENTIALS).delete(SYNC).delete(APP_LANGUAGES).delete(SUPPORT_LANGUAGES).delete(SUPPORT_SOURCE);
    });
  }
  clean() {
    const {
      [PRICING]: planType,
      [PLAN_COMPATIBLE]: compatible,
      [TIERS]: tiers,
      [SEARCH]: search,
      [SORT_FIELD]: sortField
    } = this;
    return this.withMutations(mutableFilters => {
      if (sortField) {
        if (sortField === SortField.RELEVANCE && !search) {
          mutableFilters.delete(SORT_FIELD).delete(SORT_ORDER);
        }
        if (sortField === SortField.RANDOM) {
          mutableFilters.delete(SORT_ORDER);
        }
        if (sortField === SortField.PRICE && planType === PlanType.FREE) {
          mutableFilters.delete(SORT_FIELD).delete(SORT_ORDER);
        }
      }
      if (compatible && tiers) {
        mutableFilters.delete(TIERS);
      }
    });
  }
  getValidFilterTagKeys() {
    return filterKeyOrder.filter(key => {
      const value = this.get(key);
      if (key === SORT_FIELD || key === SORT_ORDER || key === SEARCH || key === SHOW_PREFERRED_LANG_FIRST) {
        return false;
      }
      if (value == null || value === false) {
        return false;
      }
      if (Immutable.Set.isSet(value)) {
        return !value.isEmpty();
      }
      return true;
    });
  }
}
export { filterKeyOrder };
export default StorefrontFilters;